import { useState, useEffect, useRef } from 'react';
import { Box, TextField, InputAdornment, IconButton, MenuItem, Menu } from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { FilterType } from './TableHeader';
import type { ColumnFilterWithDisplayLabel, FilterConfig, FilterOption } from './TableHeader';

interface FilterMenuProps {
    anchorEl: HTMLElement | null; // Anchor element for the menu
    filter: FilterConfig;
    columnFilters: ColumnFilterWithDisplayLabel[];
    onClose: () => void;
    onFilterChange: (filterId: string, value: string, label: string) => void;
}

/**
 * FilterMenu component that displays a menu of filter options for a given filter configuration.
 *
 * @param {FilterMenuProps} props
 * @returns {JSX.Element}
 */
const FilterMenu = (props: FilterMenuProps) => {
    const { anchorEl, filter, columnFilters, onClose, onFilterChange } = props;

    const [searchText, setSearchText] = useState('');
    const [filteredOptions, setFilteredOptions] = useState<FilterOption[]>(filter.options || []);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Get current filter value
    const getFilterValue = (filterId: string): string => {
        const currentFilter = columnFilters.find((f) => f.id === filterId);
        return currentFilter?.value ? (currentFilter.value as string) : '';
    };

    // Update filtered options when search text changes
    useEffect(() => {
        if (filter.type === FilterType.COMBOBOX && searchText) {
            const filtered = (filter.options || []).filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase()));
            setFilteredOptions(filtered);
        } else {
            setFilteredOptions(filter.options || []);
        }
    }, [searchText, filter]);

    // Focus the search input when the menu opens
    useEffect(() => {
        if (anchorEl && filter.type === FilterType.COMBOBOX) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [anchorEl, filter.type]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const handleClearSearch = () => {
        setSearchText('');
    };

    const handleFilterOptionSelect = (option: FilterOption | string) => {
        if (typeof option === 'string') {
            onFilterChange(filter.id, option, option);
            return;
        }

        onFilterChange(filter.id, option.value, option.label);
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            sx={{
                maxWidth: filter?.maxWidth || '200px',
                maxHeight: '400px',
                '& .MuiPaper-root': {
                    maxHeight: '400px',
                    minWidth: filter?.maxWidth || '200px',
                },
            }}
            disableAutoFocusItem
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            aria-label={`Filter options for ${filter?.label.toLowerCase()}`}
            data-testid={`filter-options-${filter?.label.toLowerCase()}`}
        >
            {filter.type === FilterType.COMBOBOX && (
                <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search..."
                        value={searchText}
                        onChange={handleSearchChange}
                        variant="outlined"
                        inputRef={searchInputRef}
                        autoFocus
                        onKeyDown={(e) => e.stopPropagation()}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={handleClearSearch} edge="end" aria-label="clear search">
                                            <Clear fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            },
                        }}
                    />
                </Box>
            )}
            {!filter.hideAllOption && (
                <MenuItem
                    onClick={() => handleFilterOptionSelect('')}
                    selected={getFilterValue(filter.id) === '' || getFilterValue(filter.id) === 'all'}
                >
                    All
                </MenuItem>
            )}
            {filteredOptions.map((option) => (
                <MenuItem
                    key={String(option.value)}
                    onClick={() => handleFilterOptionSelect(option)}
                    selected={getFilterValue(filter.id) === String(option.value)}
                    sx={option.backgroundColor ? { bgcolor: option.backgroundColor } : undefined}
                >
                    {option.label}
                </MenuItem>
            ))}
            {filteredOptions.length === 0 && searchText && (
                <Box sx={{ px: 2, py: 1, textAlign: 'center', color: 'text.secondary', width: '100%' }}>No matches found</Box>
            )}
        </Menu>
    );
};

export default FilterMenu;
